import { Platform, AppState, type AppStateStatus } from 'react-native';

/**
 * CORE-822 — «the app returned to the foreground», one signal across platforms.
 *
 * The mobile capability layer needs this where the web half did not: on native
 * the JS interval is frozen by the OS while the app is backgrounded, so a
 * long background sleep would otherwise leave a stale (possibly widened)
 * capability set in memory. Re-fetching on foreground is what makes that safe —
 * on return the store re-fetches in full, or (offline/error) collapses to the
 * safe baseline. Either way a stale wide grant never outlives a return to focus.
 *
 * Kept as a pure, hook-free helper (mirrors the AppState pattern in
 * `useWatchPartyHub`/`useLiveRoom`) so it is unit-testable without a hook
 * renderer — the repo has none; see the `fetchCalendarDotsCached` precedent.
 *
 * @param onForeground called each time the app becomes foreground/active.
 * @returns an unsubscribe that removes every listener it registered.
 */
export function subscribeAppForeground(onForeground: () => void): () => void {
  // Native (iOS/Android): AppState 'active' fires on app foreground. The
  // `.remove()` shape is the RN 0.71+ subscription API.
  if (Platform.OS !== 'web') {
    const sub = AppState.addEventListener('change', (s: AppStateStatus) => {
      if (s === 'active') onForeground();
    });
    return () => {
      sub.remove();
    };
  }

  // Web: window focus + document visibility, matching the original CORE-817
  // hook behaviour exactly (focus always refreshes; visibility only when the
  // tab is not hidden). Guarded for non-DOM environments (SSR / node tests).
  if (typeof window === 'undefined') return () => {};

  const onFocus = () => {
    onForeground();
  };
  window.addEventListener('focus', onFocus);

  let onVisibility: (() => void) | undefined;
  if (typeof document !== 'undefined') {
    onVisibility = () => {
      if (!document.hidden) onForeground();
    };
    document.addEventListener('visibilitychange', onVisibility);
  }

  return () => {
    window.removeEventListener('focus', onFocus);
    if (onVisibility && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  };
}
